import { ActionContext, ActionTree } from 'vuex';
import { LeadsMutationsType, Mutations } from './mutations';
import { State } from './state';
import { State as GeneralState } from '@/store/modules/general';
import { RootState } from '@/store';
import {
  Banker,
  LeadsApiOptions,
  LeadsOverviewApiResponse,
  SetLeadFieldValuePayload,
  SingleLead,
} from './types';
import ServerAPI, {
  combineQueryParams,
  handleError,
} from '@/services/serverAPI';
import { TaskStatus } from '@/enums/Tasks';
import { formatSavePayload } from '@/helpers/leads';
import { getters as generalGetters } from '@/store/modules/general/getters';
import { SearchType } from '@/enums/Global';
import { format } from '@/helpers/format'


type AugmentedAction = {
  commit<K extends keyof Mutations>(
    key: K,
    payload: Parameters<Mutations[K]>[1]
  ): ReturnType<Mutations[K]>;
} & Omit<ActionContext<State, RootState>, 'commit'>;

export enum LeadsActionTypes {
  GET_LEADS = 'GET_LEADS',
  GET_NEW_LEADS_COUNT = 'GET_NEW_LEADS_COUNT',
  CLEAR_LEADS_STATE = 'CLEAR_LEADS_STATE',
  GET_BANKERS = 'GET_BANKERS',
  SET_LEADS_LOADING = 'SET_LEADS_LOADING',
  GET_LEAD = 'GET_LEAD',
  SET_LEAD_FIELD_VALUE = 'SET_LEAD_FIELD_VALUE',
  SAVE_LEAD = 'SAVE_LEAD',
  SET_LEAD_UNREACHABLE = 'SET_LEAD_UNREACHABLE',
  SET_CALL_BACK = 'SET_CALL_BACK',
  REJECT_LEAD = 'REJECT_LEAD',
  REOPEN_LEAD = 'REOPEN_LEAD',
  SUBMIT_LEAD = 'SUBMIT_LEAD',
  CLEAR_INDIVIDUAL_LEAD_STATE = 'CLEAR_INDIVIDUAL_LEAD_STATE',
}

export type Actions = {
  [LeadsActionTypes.GET_LEADS](
    { commit }: AugmentedAction,
    payload: {
      options: LeadsApiOptions;
      generalState: GeneralState;
    }
  ): Promise<void>;
  [LeadsActionTypes.GET_NEW_LEADS_COUNT]({
    commit,
  }: AugmentedAction): Promise<void>;
  [LeadsActionTypes.CLEAR_LEADS_STATE]({
    commit,
  }: AugmentedAction): Promise<void>;
  [LeadsActionTypes.GET_BANKERS]({ commit }: AugmentedAction): Promise<void>;
  [LeadsActionTypes.SET_LEADS_LOADING](
    { commit }: AugmentedAction,
    payload: boolean
  ): Promise<void>;
  [LeadsActionTypes.GET_LEAD](
    { commit }: AugmentedAction,
    leadId: string
  ): Promise<void>;
  [LeadsActionTypes.SET_LEAD_FIELD_VALUE](
    { commit }: AugmentedAction,
    payload: SetLeadFieldValuePayload
  ): Promise<void>;
  [LeadsActionTypes.SAVE_LEAD](
    { commit }: AugmentedAction,
    payload: SingleLead
  ): Promise<void>;
  [LeadsActionTypes.SET_LEAD_UNREACHABLE](
    { commit }: AugmentedAction,
    leadId: string
  ): Promise<void>;
  [LeadsActionTypes.SET_CALL_BACK](
    { commit }: AugmentedAction,
    payload: { leadId: string; date: string; time: string }
  ): Promise<void>;
  [LeadsActionTypes.REJECT_LEAD](
    { commit }: AugmentedAction,
    payload: { leadId: string; reason: string }
  ): Promise<void>;
  [LeadsActionTypes.SUBMIT_LEAD](
    { commit }: AugmentedAction,
    payload: SingleLead
  ): Promise<void>;
  [LeadsActionTypes.CLEAR_INDIVIDUAL_LEAD_STATE]({
    commit,
  }: AugmentedAction): Promise<void>;
  [LeadsActionTypes.REOPEN_LEAD](
    { commit }: AugmentedAction,
    payload: SingleLead
  ): Promise<void>;
};

export const actions: ActionTree<State, RootState> & Actions = {
  async [LeadsActionTypes.GET_LEADS]({ commit }, payload) {
    commit(LeadsMutationsType.SET_LEADS_SETTINGS, payload.options);
    commit(LeadsMutationsType.SET_LEADS_LOADING, true);

    try {
      const paramString = combineQueryParams({
        size: payload.options.pageSize,
        start: payload.options.pageStart,
        ['sort_descending']: payload.options.sortDesc,
        ['sort_by']: payload.options.sortKey,
      });

      let filters = '';

      if (!payload.options.viewDone) {
        filters += `&filters=task_status:${TaskStatus.TODO},${TaskStatus.NOT_STARTED},${TaskStatus.IN_PROGRESS}`;
      }

      if (payload.options.isManaged == true) {
        const isManagedFilter = `is_managed:true`;
        filters += `&filters=${isManagedFilter}`;
      }

      let searchTerm = '';

      if (payload.options.searchTerm) {
        if (payload.options.searchType == SearchType.LEAD) {
          const matchingLeadIds = generalGetters.getLeadIdsByNameSearch(
            payload.generalState,
            payload.options.searchTerm
          );

          searchTerm = matchingLeadIds.join(',');
        } else {
          searchTerm = payload.options.searchTerm;
        }
        filters += `&search_field=${payload.options.searchType}&search_string=${searchTerm}`;
      }

      const leadsRes: LeadsOverviewApiResponse = await ServerAPI.get(
        `/leads${paramString}${filters}`
      );

      commit(LeadsMutationsType.SET_LEADS, {
        leads: leadsRes.results,
        count: leadsRes.count,
      });
    } catch (e) {
      handleError(e);
      commit(LeadsMutationsType.SET_LEADS, { leads: [], count: 0 });
    } finally {
      commit(LeadsMutationsType.SET_LEADS_LOADING, false);
    }
  },
  async [LeadsActionTypes.GET_NEW_LEADS_COUNT]({ commit }) {
    try {
      const paramString = combineQueryParams({
        size: 1,
        start: 0,
      });

      let filters = '';
      filters += `&filters=task_status:${TaskStatus.TODO},${TaskStatus.NOT_STARTED}`;
      filters += '&filters=is_managed:true'; // JTI-5553

      const leadsRes: LeadsOverviewApiResponse = await ServerAPI.get(
        `/leads${paramString}${filters}`
      );

      commit(LeadsMutationsType.SET_NEW_LEADS_COUNT, leadsRes.count);
    } catch (e) {
      commit(LeadsMutationsType.SET_NEW_LEADS_COUNT, 0);
    }
  },
  async [LeadsActionTypes.CLEAR_LEADS_STATE]({ commit }) {
    commit(LeadsMutationsType.CLEAR_LEADS_STATE, undefined);
  },
  async [LeadsActionTypes.GET_BANKERS]({ commit }) {
    commit(LeadsMutationsType.SET_BANKERS_LOADING, true);
    try {
      const bankers: Array<Banker> = await ServerAPI.get('/meta/bankers');
      commit(LeadsMutationsType.SET_BANKERS, bankers);
    } catch (e) {
      handleError(e);
      commit(LeadsMutationsType.SET_BANKERS, []);
    } finally {
      commit(LeadsMutationsType.SET_BANKERS_LOADING, false);
    }
  },
  async [LeadsActionTypes.SET_LEADS_LOADING]({ commit }, payload) {
    commit(LeadsMutationsType.SET_LEADS_LOADING, payload);
  },
  async [LeadsActionTypes.GET_LEAD]({ commit }, leadId) {
    commit(LeadsMutationsType.SET_LEAD_LOADING, true);

    try {
      const lead: SingleLead = await ServerAPI.get(`/leads/${leadId}`);
      commit(LeadsMutationsType.SET_LEAD, lead);
    } catch (e) {
      handleError(e);
      commit(LeadsMutationsType.SET_LEAD, {} as SingleLead);
    } finally {
      commit(LeadsMutationsType.SET_LEAD_LOADING, false);
    }
  },
  async [LeadsActionTypes.SET_LEAD_FIELD_VALUE]({ commit }, payload) {
    if (payload && payload.field !== '') {
      commit(LeadsMutationsType.SET_LEAD_FIELD_VALUE, payload);
    } else {
      // eslint-disable-next-line no-console
      console.error('Cannot set lead field value as some details are missing');
    }
  },
  async [LeadsActionTypes.SAVE_LEAD](_, payload) {
    try {
      const formattedPayload = formatSavePayload(payload);
      await ServerAPI.patch(
        `/leads/${payload.leadId.dataValue}/save`,
        formattedPayload
      );
    } catch (e) {
      handleError(e);
    }
  },
  async [LeadsActionTypes.SET_LEAD_UNREACHABLE](_, leadId) {
    try {
      await ServerAPI.get(`/leads/${leadId}/interactions/not_reached`);
    } catch (e) {
      handleError(e);
    }
  },
  async [LeadsActionTypes.SET_CALL_BACK](_, payload) {
    try {
      const data = {
        interactionDate: format(new Date(payload.date), 'yyyy-MM-dd'),
        interactionTime: payload.time,
        interactionType: 'PHONE',
      };
      await ServerAPI.post(
        `/leads/${payload.leadId}/interactions/reschedule`,
        data
      );
    } catch (e) {
      handleError(e);
    }
  },
  async [LeadsActionTypes.REJECT_LEAD](_, payload) {
    try {
      await ServerAPI.post(`/leads/${payload.leadId}/reject`, {
        rejectionReason: payload.reason,
      });
    } catch (e) {
      handleError(e);
    }
  },
  async [LeadsActionTypes.REOPEN_LEAD](_, payload) {
    try {
      await ServerAPI.post(`/leads/${payload.leadId.dataValue}/reopen`, {});
    } catch (e) {
      handleError(e);
    }
  },
  async [LeadsActionTypes.SUBMIT_LEAD](_, payload) {
    try {
      const formattedPayload = formatSavePayload(payload);
      await ServerAPI.patch(
        `/leads/${payload.leadId.dataValue}/submit`,
        formattedPayload
      );
    } catch (e) {
      handleError(e);
    }
  },
  async [LeadsActionTypes.CLEAR_INDIVIDUAL_LEAD_STATE]({ commit }) {
    commit(LeadsMutationsType.CLEAR_INDIVIDUAL_LEAD_STATE, undefined);
  },
};
